import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { PostVisitReflectionDialog } from "@/components/PostVisitReflectionDialog";
import { PostVisitReflectionPrompt } from "@/components/PostVisitReflectionPrompt";
import { useBottomPromptSlot } from "@/components/BottomPromptSlot";

interface PendingReflection {
  id: string;
  cafe_id: string;
  cafe_name: string;
  checked_in_at: string;
  cafe_latitude: number | null;
  cafe_longitude: number | null;
}

interface PostVisitReflectionContextValue {
  hasPendingReflection: boolean;
  triggerReflection: () => void;
}

const PostVisitReflectionContext = createContext<PostVisitReflectionContextValue>({
  hasPendingReflection: false,
  triggerReflection: () => {},
});

export const usePostVisitReflection = () => useContext(PostVisitReflectionContext);

/** Calculate distance between two GPS points in meters (Haversine) */
function getDistanceMeters(
  lat1: number, lon1: number,
  lat2: number, lon2: number
): number {
  const R = 6371000;
  const toRad = (v: number) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const DISTANCE_THRESHOLD_METERS = 200;
const TIME_FALLBACK_HOURS = 2;

interface PostVisitReflectionProviderProps {
  children: ReactNode;
}

/** Wrapper that registers with the bottom prompt slot */
function PostVisitSlottedPrompt(props: { cafeName: string; cafeId?: string; onTap: () => void; onDismiss: () => void }) {
  const { register, unregister, isVisible } = useBottomPromptSlot();

  useEffect(() => {
    register("post-visit");
    return () => unregister("post-visit");
  }, [register, unregister]);

  if (!isVisible("post-visit")) return null;

  return <PostVisitReflectionPrompt {...props} />;
}

export function PostVisitReflectionProvider({ children }: PostVisitReflectionProviderProps) {
  const { user, isLoading: authLoading } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hasShownThisSession, setHasShownThisSession] = useState(false);
  const [gpsChecked, setGpsChecked] = useState(false);
  const [userFarEnough, setUserFarEnough] = useState(false);

  // Query for pending reflections — only where vibe data is missing
  const { data: pendingReflection, refetch } = useQuery({
    queryKey: ["pending-reflection", user?.id],
    queryFn: async (): Promise<PendingReflection | null> => {
      if (!user?.id) return null;

      // Get the most recent check-in that hasn't been reflected on
      // AND where the user skipped the vibe check (all key fields null)
      const { data, error } = await supabase
        .from("check_ins")
        .select(`
          id,
          cafe_id,
          checked_in_at,
          focus_rating,
          wifi_speed,
          noise_level,
          cafes:cafe_id (
            name,
            latitude,
            longitude
          )
        `)
        .eq("user_id", user.id)
        .eq("reflection_completed", false)
        .eq("is_complete", true)
        .order("checked_in_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("[PostVisitReflectionProvider] Query error:", error);
        return null;
      }

      if (!data) return null;

      // If vibe data already exists, auto-mark as completed and skip
      if (data.focus_rating != null || data.wifi_speed != null || data.noise_level != null) {
        await supabase
          .from("check_ins")
          .update({ reflection_completed: true })
          .eq("id", data.id);
        return null;
      }

      return {
        id: data.id,
        cafe_id: data.cafe_id,
        cafe_name: (data.cafes as any)?.name || "Unknown Cafe",
        checked_in_at: data.checked_in_at,
        cafe_latitude: (data.cafes as any)?.latitude ?? null,
        cafe_longitude: (data.cafes as any)?.longitude ?? null,
      };
    },
    enabled: !!user?.id && !authLoading,
    staleTime: 1000 * 60 * 5,
  });

  // GPS distance check
  const checkDistance = useCallback(() => {
    if (!pendingReflection) return;

    const { cafe_latitude, cafe_longitude, checked_in_at } = pendingReflection;

    // If cafe has no coordinates, fall back to time check
    if (cafe_latitude == null || cafe_longitude == null) {
      const hoursSince =
        (Date.now() - new Date(checked_in_at).getTime()) / (1000 * 60 * 60);
      setUserFarEnough(hoursSince >= TIME_FALLBACK_HOURS);
      setGpsChecked(true);
      return;
    }

    if (!navigator.geolocation) {
      // No geolocation API — use time fallback
      const hoursSince =
        (Date.now() - new Date(checked_in_at).getTime()) / (1000 * 60 * 60);
      setUserFarEnough(hoursSince >= TIME_FALLBACK_HOURS);
      setGpsChecked(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const dist = getDistanceMeters(
          position.coords.latitude,
          position.coords.longitude,
          cafe_latitude,
          cafe_longitude
        );
        setUserFarEnough(dist >= DISTANCE_THRESHOLD_METERS);
        setGpsChecked(true);
      },
      () => {
        // GPS denied/failed — use time fallback
        const hoursSince =
          (Date.now() - new Date(checked_in_at).getTime()) / (1000 * 60 * 60);
        setUserFarEnough(hoursSince >= TIME_FALLBACK_HOURS);
        setGpsChecked(true);
      },
      { timeout: 5000, enableHighAccuracy: false }
    );
  }, [pendingReflection]);

  // Trigger GPS check when we have a pending reflection
  useEffect(() => {
    if (pendingReflection && !gpsChecked) {
      checkDistance();
    }
  }, [pendingReflection, gpsChecked, checkDistance]);

  // Show prompt once GPS check passes
  useEffect(() => {
    if (
      pendingReflection &&
      gpsChecked &&
      userFarEnough &&
      !hasShownThisSession &&
      !showPrompt &&
      !dialogOpen
    ) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
        setHasShownThisSession(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [pendingReflection, gpsChecked, userFarEnough, hasShownThisSession, showPrompt, dialogOpen]);

  // Mark reflection as completed in DB
  const markReflectionCompleted = async () => {
    if (!pendingReflection) return;

    await supabase
      .from("check_ins")
      .update({ reflection_completed: true })
      .eq("id", pendingReflection.id);

    refetch();
  };

  const handlePromptTap = () => {
    setShowPrompt(false);
    setDialogOpen(true);
  };

  const handlePromptDismiss = async () => {
    setShowPrompt(false);
    await markReflectionCompleted();
  };

  const handleDialogClose = async (open: boolean) => {
    setDialogOpen(open);
    if (!open && pendingReflection) {
      await markReflectionCompleted();
    }
  };

  const handleComplete = () => {
    refetch();
  };

  const triggerReflection = () => {
    if (pendingReflection) {
      setDialogOpen(true);
    }
  };

  return (
    <PostVisitReflectionContext.Provider
      value={{
        hasPendingReflection: !!pendingReflection,
        triggerReflection,
      }}
    >
      {children}

      {showPrompt && pendingReflection && (
        <PostVisitSlottedPrompt
          cafeName={pendingReflection.cafe_name}
          cafeId={pendingReflection.cafe_id}
          onTap={handlePromptTap}
          onDismiss={handlePromptDismiss}
        />
      )}

      <PostVisitReflectionDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        pendingReflection={pendingReflection || null}
        onComplete={handleComplete}
      />
    </PostVisitReflectionContext.Provider>
  );
}
